import { faCheckCircle, faGift, faInfoCircle, faPaperPlane, faPrint, IconDefinition } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useState } from 'react'
import { ButtonPrimary, ButtonRedSecondary, ButtonSecondary } from '../../../../../../baseComponents/Button'
import { DisplayPriceGettingParams } from '../../../../../../baseComponents/Displays/DisplayPrice'
import { ModalMediumCenteredLoading } from '../../../../../../baseComponents/Modal'
import { ModalOpenedTy } from '../../../../../../baseComponents/Modal/types'
import TwinIcon, { TwinIconToolTip } from '../../../../../../baseComponents/TwinIcon'
import { RowData } from '../../../../../../baseComponents/TwinTable/types'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import twinFetchPost, { twinFetchPostPrinter } from '../../../../../../utils/globals/data'
import { SetEditDeleteModalTy } from '../../../../../../utils/hooks/useEditDeleteModal'
import useIsLoading from '../../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { dictIcon } from '../../../../../../utils/icons'
import ModalSendTicketTPV from './ModalSendTicketTPV'
import { InputTableURLsTable } from '../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import LoadingSpinner from '../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { getConfigParam, getUserPermissions } from '../../../../../../utils/reducers/getters'
import PermissionChecker from '../../../../../../baseComponents/PermissionChecker'
import './basePaymentMethodInfoTPV.sass'

interface BasePaymentMethodInfoTPVProps extends BasePaymentMethodInfoTPVLogicProps {
    setOpenModal: SetEditDeleteModalTy
    translations: {
        title: string,
        numOrderTitle: string,
        textButton: {
            send: string,
            gift: string,
            print: string
        }
    }
    handleCloseModal?: () => void
    inputTableURLs: InputTableURLsTable
    downloadPdfUrl?: string
}

const BasePaymentMethodInfoTPV: React.FC<BasePaymentMethodInfoTPVProps> = ({ setOpenModal, allRowData, translations, handleCloseModal, functionButtonsURLs, inputTableURLs, isReservedRefund, downloadPdfUrl }) => {
    const { t } = useTwinTranslation()
    const renderThis: JSX.Element[] = []
    const renderThisRefund: JSX.Element[] = []
    const isPartialCharge = allRowData?.partialCharge && parseFloat(String(allRowData?.pendingAssigned)) && parseFloat(String(allRowData?.totalAssigned)) 
    const { openEditModal, setOpenEditModal, handlePrintTicket, handlePrintGiftTicket, loading, companyEmail } = useBasePaymentMethodInfoTPVLogic({ functionButtonsURLs , allRowData, isPartialCharge, isReservedRefund})
    for (const key in allRowData?.paymentMethodInfo || []) {
        const element = allRowData?.paymentMethodInfo[parseInt(key)]
        if (element) {
            if (element?.operationType === 'ticketRefund') {
                renderThisRefund.push(<PaymentMethodInfoRow title={t('refund', 'Devolución') + ' ' + element.name} CurrencyId={element.CurrencyId || element.extraData?.CurrencyId || allRowData?.CurrencyId} value={element.quantity} icon={dictIcon[element.icon || 'faMoneyBill']} key={key} />)
            } else {
                renderThis.push(<PaymentMethodInfoRow title={element.name} CurrencyId={element.CurrencyId || element.extraData?.CurrencyId || allRowData?.CurrencyId} value={element.quantity} icon={dictIcon[element.icon || 'faMoneyBill']} key={key} />)
            }

        }
    }
    return (
        <ModalMediumCenteredLoading loading={false} className='modal_payment_info flex_height p-30' opened={true} setOpened={() => setOpenModal(null)} onClickOut={false}>
            <div className='text-green-43 flex flex-col gap-y-5'>
                <TwinIcon icon={faCheckCircle} className='h-60' />
                <span className='regular24'>{translations.title}</span>
            </div>
            <div className='flex flex-row items-center justify-between w-full gap-10 flex-wrap border-b border-gray-D6 py-10'>
                <span className='medium14'>{translations.numOrderTitle}</span>
                <span>{allRowData?.headerData?.full_assigned_number}</span>
            </div>
            {allRowData?.headerData?.Customer?.id ? <div className='flex flex-row items-center justify-between w-full gap-10 flex-wrap py-10 border-b border-gray-D6'>
                <span className='medium14'><TwinTrans transKey='customer'>Cliente</TwinTrans></span>
                <span>{allRowData?.headerData?.Customer?.fiscal_name || ((allRowData?.headerData?.Customer?.name || '') + ' ' + (allRowData?.headerData?.Customer?.surname || ''))}</span>
            </div> : null}
            {renderThis}
            <PaymentMethodInfoRow title={t('total', 'Total')} CurrencyId={allRowData?.CurrencyId} value={allRowData?.myTotalPayments?.total} className='payment_method_totals'/>
            {renderThisRefund.length ? <RefundPaymentMethod children={renderThisRefund} CurrencyId={allRowData?.CurrencyId} total_clean_with_vat={allRowData?.headerData?.total_clean_with_vat} /> : null}
            <div className='flex flex-col gap-y-20 mt-30'>
                {loading ? <LoadingSpinner/> :
                    <div className='flex gap-x-20 flex-auto'>
                        <PaymentMethodInfoButton icon={faPaperPlane} text={translations.textButton.send} functionButton={() => setOpenEditModal(true)} loading={loading} disabled={companyEmail ? false : true} errorInfo={companyEmail ? undefined : t('dontHaveContactEmailConfigured', 'No tienes configurado el email de contacto')} />
                        {translations.textButton.gift ? <PaymentMethodInfoButton icon={faGift} text={translations.textButton.gift} functionButton={handlePrintGiftTicket} loading={loading} /> : null}
                        <PaymentMethodInfoButton icon={faPrint} text={translations.textButton.print} functionButton={handlePrintTicket} loading={loading} />
                    </div>
                }
                <FooterProcessButtons setOpenModal={setOpenModal} handleCloseModal={handleCloseModal} allRowData={allRowData} />
            </div>
            {openEditModal ? <ModalSendTicketTPV allRowData={allRowData || {}} setOpened={setOpenEditModal} url={functionButtonsURLs.send} inputTableURLs={inputTableURLs} OrderId={allRowData?.OrderId} isReservedRefund={isReservedRefund} downloadPdfUrl={downloadPdfUrl} />: null}
        </ModalMediumCenteredLoading>
    )
}

interface BasePaymentMethodInfoTPVLogicProps {
    functionButtonsURLs: {
        send: string
        gift: string
        print: string
    }
    allRowData?: RowData
    isPartialCharge?: boolean
    isReservedRefund?: boolean
}

const useBasePaymentMethodInfoTPVLogic = ({ functionButtonsURLs, allRowData, isReservedRefund }: BasePaymentMethodInfoTPVLogicProps) => {
    const { t } = useTwinTranslation()
    const { startLoading, endLoading, loading } = useIsLoading()
    const errorMessage = t('couldNotConnectWithPrinter', 'No se ha podido conectar con la impresora')
    const [openEditModal, setOpenEditModal] = useState<ModalOpenedTy>(null)
    const companyEmail = getConfigParam('emailCompanyEmail')

    const handlePrintTicket = useCallback(async () => {
        startLoading?.()
        const result = await twinFetchPost(functionButtonsURLs.print, { id: allRowData?.OrderId, isReservedRefund, WorkingCenterId: allRowData?.WorkingCenterId })
        if (result && result.status === 367) {
            const body = await result.json()
            if (body instanceof Array || isReservedRefund) {
                for (const ticket of body || []) {
                    await twinFetchPostPrinter('/print', ticket, errorMessage)
                }
            } else {
                await twinFetchPostPrinter('/print', body, errorMessage)
            }
        }
        endLoading()
    }, [startLoading, endLoading, errorMessage, allRowData?.OrderId, functionButtonsURLs.print, isReservedRefund, allRowData?.WorkingCenterId])

    const handlePrintGiftTicket = useCallback(async () => {
        startLoading?.()
        const result = await twinFetchPost(functionButtonsURLs.gift, { id: allRowData?.OrderId, isReservedRefund, WorkingCenterId: allRowData?.WorkingCenterId })
        if (result && result.status === 367) {
            const body = await result.json()
            await twinFetchPostPrinter('/print', body, errorMessage)
        }
        endLoading()
    }, [startLoading, endLoading, errorMessage, allRowData?.OrderId, functionButtonsURLs.gift, isReservedRefund, allRowData?.WorkingCenterId])

    return { openEditModal, setOpenEditModal, handlePrintTicket, handlePrintGiftTicket, loading, companyEmail }
}


interface PaymentMethodInfoRowProps {
    title: string
    CurrencyId: string
    value: number
    icon?: IconDefinition
    className?: string
}

const PaymentMethodInfoRow: React.FC<PaymentMethodInfoRowProps> = ({ title, CurrencyId, value, icon, className }) => {
    return (
        <div className={'payment_method_info_row flex flex-row items-center justify-between w-full gap-10 flex-wrap border-b border-gray-D6 py-10 ' + className} >
            <div className='flex items-center gap-x-10 medium14'>
                {icon ? <TwinIcon icon={icon} className='w-24 h-24' /> : null}
                <span className=''>{title}</span>
            </div>
            <span className={'flex'}>
                <DisplayPriceGettingParams CurrencyId={CurrencyId} value={value} with_vat={false} />
            </span>
        </div>
    )
}

interface PaymentMethodInfoButtonProps {
    icon: IconDefinition
    text: string
    functionButton: any
    loading: boolean
    disabled?: boolean
    errorInfo?: string
}

const PaymentMethodInfoButton: React.FC<PaymentMethodInfoButtonProps> = ({ icon, text, functionButton, disabled, errorInfo }) => {
    return (
        <ButtonSecondary className='modal_payment_info_button flex gap-x-10 flex-auto' onClick={functionButton} disabled={disabled}>
            {!errorInfo ? <TwinIcon icon={icon} className='w-18 h-18' /> : <TwinIconToolTip message={errorInfo} id={'help'} icon={faInfoCircle} className='inline-block ' classNameTooltip='max-w-300' />}
            <span className='regular14 '>{text}</span>
        </ButtonSecondary>
    )
}

interface RefundPaymentMethodProps {
    children: JSX.Element[]
    CurrencyId: string
    total_clean_with_vat: number
}

const RefundPaymentMethod: React.FC<RefundPaymentMethodProps> = ({ children, total_clean_with_vat, CurrencyId }) => {
    const { t } = useTwinTranslation()
    return (
        <div>
            <div>{children}</div>
            <PaymentMethodInfoRow title={t('totalRestTotal', 'Total restante')} CurrencyId={CurrencyId} value={total_clean_with_vat} className='payment_method_totals' />
        </div>
    )
}

interface FooterProcessButtonsProps extends FooterProcessButtonsLogicProps {
}

const FooterProcessButtons: React.FC<FooterProcessButtonsProps> = ({ allRowData, handleCloseModal, setOpenModal }) => {
    const { handleOpenRegularizeModal } = useFooterProcessButtonsLogic({ handleCloseModal, setOpenModal, allRowData })
    return (
        <div className='flex gap-x-20'>
            {allRowData?.canRegularize ? <PermissionChecker userPermissions={getUserPermissions('sellOther.canRegularizeSellTicket')} permission={'read'}>
                <ButtonRedSecondary className='w-full modal_payment_info_button regular16 regularize_button' onClick={handleOpenRegularizeModal}><TwinTrans transKey='changePaymentMethods'>Cambiar métodos de pago</TwinTrans></ButtonRedSecondary>
            </PermissionChecker> : null}
            <ButtonPrimary className='w-full modal_payment_info_button' onClick={() => { handleCloseModal?.() || setOpenModal(null) }}><TwinTrans transKey='finalize'>Finalizar</TwinTrans></ButtonPrimary>
        </div>
    )
}

interface FooterProcessButtonsLogicProps {
    handleCloseModal?: () => void
    setOpenModal: SetEditDeleteModalTy
    allRowData?: RowData
}

const useFooterProcessButtonsLogic = ({ allRowData, setOpenModal }: FooterProcessButtonsLogicProps) => {
    const handleOpenRegularizeModal = useCallback(() => {
        setOpenModal({ type: 'create', allRowData: allRowData || {} })
    }, [setOpenModal, allRowData])

    return { handleOpenRegularizeModal }
}



export default BasePaymentMethodInfoTPV